<template>
  <BasePanel :class="prefixCls" v-bind="$attrs" :hideTitle="hideTitle">
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data"></slot>
    </template>

    <template #title>
      <template v-if="titleSchema && titleValue">
        <ReadonlyText
          :value="titleValue"
          v-bind="omit(titleSchema.componentProps, ['suffix'])"
          class="text-18px font-500"
        />
        <div class="text-[0.875em] text-[#7A8494]">
          {{ t(titleSchema.label as string) }}
        </div>
      </template>

      <DictTag
        v-if="statusSchema"
        class="!mr-0"
        :code="statusSchema['dataTypeCode']"
        v-bind="statusSchema.componentProps"
        :text="detailInfo[statusSchema.field]"
      />

      <div v-if="infoTagOptions.length > 0" class="flex">
        <BaseTag
          v-for="item of infoTagOptions"
          :key="item.label"
          :text="item.value"
          class="!text-[#FF752B]"
          bordered
        />
      </div>
    </template>
    <ReadonlyForm
      :class="[rowTruncate && 'form-row-truncate', maxRow2 && 'max-row-2']"
      inset
      @register="registerForm"
    />
    <slot name="detailFooter" v-bind="{ record: detailInfo }"></slot>
  </BasePanel>
</template>

<script setup lang="ts">
  import { get } from 'lodash-es';
  import { computed, shallowRef } from 'vue';
  import type { Option } from '@mts-fe/types';
  import { isArray, isNil, omit, replaceUrl } from '@mts-fe/utils';
  import { BasePanel, BaseTag, DictTag, ReadonlyForm } from '@mts/components/Base';
  import { FormProps, FormSchema, ReadonlyText, useForm } from '@mts/components/Form';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { t } from '@mts/hooks/web/useI18n';

  defineOptions({
    name: 'DetailPanel',
  });

  withDefaults(
    defineProps<{
      hideTitle?: boolean;
      rowTruncate?: boolean;
      maxRow2?: boolean;
    }>(),
    { rowTruncate: true },
  );

  const { prefixCls } = useDesign('detail-panel');

  const detailInfo = shallowRef<Recordable>({});
  const titleSchema = shallowRef<FormSchema>();
  const statusSchema = shallowRef<FormSchema>();
  const infoTagSchemas = shallowRef<FormSchema[]>([]);

  const titleValue = computed(() => {
    const schema = titleSchema.value;
    const info = detailInfo.value;

    if (!schema || Object.keys(info).length === 0) return;

    const {
      field,
      componentProps: { suffix = '' },
    } = schema;

    const value = info[field];
    return isNil(value) ? '' : value + replaceUrl(suffix, info);
  });

  const infoTagOptions = computed(() => {
    const schemas = infoTagSchemas.value;
    const info = detailInfo.value;

    if (!schemas.length || Object.keys(info).length === 0) return [];

    const options: Option[] = [];
    for (const schema of schemas) {
      const value = get(info, schema.field);
      const label = schema.label as string;
      if (isArray(value)) {
        for (const val of value) {
          options.push({ value: val, label: `${label}@${val}` });
        }
      } else {
        const { suffix } = schema.componentProps;
        options.push({ value: suffix ? (value ?? '-') + replaceUrl(suffix, info) : value, label });
      }
    }
    return options;
  });

  const [registerForm, formAction] = useForm();

  const setProps = async (formProps: Partial<FormProps>) => {
    const schemas = formProps.schemas;
    if (!schemas || schemas.length === 0) {
      await formAction.setProps(formProps);
      return;
    }

    let titleSchemaValue: FormSchema | undefined;
    let statusSchemaValue: FormSchema | undefined;
    const infoTagSchemasValue: FormSchema[] = [];
    const formSchemas: FormSchema[] = [];

    for (const schema of schemas) {
      const showIn = schema.showIn;
      if (showIn === 'title') {
        titleSchemaValue = schema;
      } else if (showIn === 'statusTag' || showIn === 'statusText') {
        statusSchemaValue = {
          ...schema,
          componentProps: { textMode: showIn === 'statusText', ...schema.componentProps },
        };
      } else if (showIn === 'infoTag') {
        infoTagSchemasValue.push(schema);
      } else {
        formSchemas.push(schema);
      }
    }

    titleSchema.value = titleSchemaValue;
    statusSchema.value = statusSchemaValue;
    infoTagSchemas.value = infoTagSchemasValue;

    await formAction.setProps({ ...formProps, schemas: formSchemas });
  };

  const setFieldsValue = async (value: Recordable = {}) => {
    detailInfo.value = value;
    await formAction.setFieldsValue(value);
  };

  defineExpose({
    ...formAction,
    setProps,
    setFieldsValue,
  });
</script>

<style lang="less">
  @prefix-cls: ~'@{namespace}-detail-panel';

  .@{prefix-cls} {
    display: flex;
    flex-direction: column;
    margin: 8px;
    padding: 14px 16px;
    border-radius: @border-radius;
    background-color: #fff;
    background-repeat: no-repeat;
    background-size: cover;

    &--small {
      margin: 4px;
      padding: 12px;
    }

    &--inset {
      margin: 0;
    }
  }
</style>
